import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Column } from ".";

interface FilterOption {
  key: string;
  value: string;
}

// Make the FilterButtonContent generic
function FilterButtonContent<T>({
  column,
  filters,
  filterOptions,
}: {
  column: Column<T>;
  filters: Record<string, string>;
  filterOptions: Record<string, FilterOption[]>;
}) {
  const columnKey = column.key.toString(); // Convert keyof T to string
  const value = filters[columnKey];
  const options = column.filter?.options || filterOptions[columnKey];

  if (column.filter?.multiple) {
    const selected = value ? value.split(",") : [];
    const label =
      selected.length === 0
        ? `Selecione ${column.label}`
        : selected.length === 1
          ? (options?.find(({ key }) => key === selected[0])?.value ??
            selected[0])
          : `${selected.length} selecionadas`;

    return (
      <>
        {label}
        <ChevronsUpDown className="opacity-50" />
      </>
    );
  }

  const selectedOption = options?.find(({ key }) => key === value);

  return (
    <>
      {selectedOption?.value || `Selecione ${column.label}`}
      <ChevronsUpDown className="opacity-50" />
    </>
  );
}

function FilterCommandItems<T>({
  column,
  filters,
  filterOptions,
  onFilter,
}: {
  column: Column<T>;
  filters: Record<string, string>;
  filterOptions: Record<string, FilterOption[]>;
  onFilter: (columnKey: string, value: string | null) => void; // Updated to accept null
}) {
  const columnKey = column.key.toString();
  const options = column.filter?.options || filterOptions[columnKey];

  if (!options) {
    return (
      <CommandItem key="loading" value="loading" disabled>
        Carregando...
      </CommandItem>
    );
  }

  const isMultiple = Boolean(column.filter?.multiple);
  const selectedValues =
    isMultiple && filters[columnKey] ? filters[columnKey].split(",") : [];

  const handleSelect = (key: string) => {
    if (!isMultiple) {
      onFilter(columnKey, key);
      return;
    }

    const next = selectedValues.includes(key)
      ? selectedValues.filter((value) => value !== key)
      : [...selectedValues, key];

    onFilter(columnKey, next.length ? next.join(",") : null);
  };

  return (
    <>
      {filters[columnKey] && (
        <CommandItem
          value="clear-filter"
          onSelect={() => onFilter(columnKey, null)}
          className="text-muted-foreground italic"
        >
          Limpar filtro
          <X className="ml-auto h-4 w-4" />
        </CommandItem>
      )}

      {options.map(({ value, key }) => (
        <CommandItem value={value} key={key} onSelect={() => handleSelect(key)}>
          {value}
          <Check
            className={cn(
              "ml-auto h-4 w-4",
              (
                isMultiple
                  ? selectedValues.includes(key)
                  : filters[columnKey] === key
              )
                ? "opacity-100"
                : "opacity-0",
            )}
          />
        </CommandItem>
      ))}
    </>
  );
}

// Make the main ColumnFilter component generic
function ColumnFilter<T>({
  column,
  filters,
  filterOptions,
  loading,
  onFilter,
}: {
  column: Column<T>;
  filters: Record<string, string>;
  filterOptions: Record<string, FilterOption[]>;
  loading: boolean;
  onFilter: (columnKey: string, value: string | null) => void;
}) {
  const columnKey = column.key.toString();

  return (
    <Popover key={columnKey}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={loading}
          className={cn(
            "justify-between dark:border-white dark:text-white truncate",
            !filters[columnKey] && "text-muted-foreground",
          )}
        >
          <FilterButtonContent<T>
            column={column}
            filters={filters}
            filterOptions={filterOptions}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Pesquise aqui" className="h-9" />
          <CommandList>
            <CommandEmpty>Nenhum registro encontrado.</CommandEmpty>
            <CommandGroup>
              <FilterCommandItems<T>
                column={column}
                filters={filters}
                filterOptions={filterOptions}
                onFilter={onFilter}
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Export with generic type parameter
export default ColumnFilter;
